import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box
} from '@mui/material';

type CookiePreferences = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

const LOCAL_STORAGE_KEY = 'cookiePreferences';

const CookiePreferencesModal: React.FC<{
  open?: boolean;
  onClose: () => void;
  onSave: (prefs: CookiePreferences) => void;
}> = ({ open = true, onClose, onSave }) => {
  const getInitialPrefs = (): CookiePreferences => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      try {
        return { ...{ essential: true, analytics: false, marketing: false }, ...JSON.parse(stored) };
      } catch {
        return { essential: true, analytics: false, marketing: false };
      }
    }
    return { essential: true, analytics: false, marketing: false };
  };

  const [prefs, setPrefs] = useState<CookiePreferences>(getInitialPrefs);

  useEffect(() => {
    setPrefs(getInitialPrefs());
    // eslint-disable-next-line
  }, []);

  const toggle = (key: keyof CookiePreferences) => {
    if (key === 'essential') return; // Essential is always on
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prefs));
    onSave(prefs);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cookie Preferences</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Select which types of cookies you're happy for us to use.
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={prefs.essential} disabled />}
            label={<Box component="span">Essential cookies (required)</Box>}
          />
          <FormControlLabel
            control={<Checkbox checked={prefs.analytics} onChange={() => toggle('analytics')} />}
            label={<Box component="span">Analytics cookies</Box>}
          />
          <FormControlLabel
            control={<Checkbox checked={prefs.marketing} onChange={() => toggle('marketing')} />}
            label={<Box component="span">Marketing cookies</Box>}
          />
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#bef213', color: '#212529' }}>
          Save Preferences
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookiePreferencesModal;
